import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { SubscribedWebsocket } from "simplydo/core/api/websocket";
import { hasPermission } from "simplydo/core/permissions";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { Loader } from "simplydo/react/ui/loader";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";

import api, { websocketApi } from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { useIdeaQueryParams } from "components/challenges/Challenge/Ideas/useIdeaQueryParams";
import PageLoadError from "components/lib/PageLoadError";

import Board, { IIdeaCard, ILaneData } from "./Board";
import { ProjectBoardContext } from "./ProjectBoardContext";
import ProjectBoardSetup from "./ProjectBoardSetup";
import {
  type ProjectBoardIdea,
  type ProjectBoardLaneUpdateBody,
} from "./types";

// The height of the board must be the viewports height minus the top bars
const StyledProjectBoardContainer = styled.div`
  height: calc(100vh - 210px);
  display: flex;
  flex-direction: column;
`;

const StyledLaneContainer = styled.div`
  min-height: 200px;
  flex-grow: 1;
  font-family: "Inter";
  h1,
  h2,
  h3,
  h4,
  h5 {
    font-family: "Inter";
  }
  > p {
    margin-top: 20px;
    text-align: center;
    font-size: 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin: 10px;
  > span {
    display: block;
    margin: 0 0 0 5px;
    font-weight: bold;
  }
`;

type IProject = {
  forId: string;
  ideas: OpenAPI.Schemas["Idea"][];
  ideasLoading: boolean;

  canManageChallenge?: boolean;
  projectManagementVisibility?: OpenAPI.GET<"/challenges/{id}">["response"]["projectManagementVisibility"];
  defaultLane?: NonNullable<
    OpenAPI.GET<"/challenges/{id}">["response"]["projectBoard"]
  >["defaultLane"];

  setPreviewId: Dispatch<SetStateAction<string>>;

  isSelecting: boolean;
  selectedIdeas: OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"];
  setSelectedIdeas: Dispatch<
    SetStateAction<OpenAPI.GET<"/challenges/{id}/ideas">["response"]["ideas"]>
  >;

  laneFilter?: string[];

  checkUpdateProjectLane: (
    ideaIds: string[],
    laneId: string,
    order: number | null,
  ) => void;
};

function Project({
  forId,
  ideas,
  ideasLoading,
  projectManagementVisibility,
  defaultLane,
  laneFilter,

  setPreviewId,

  isSelecting,
  selectedIdeas,
  setSelectedIdeas,

  checkUpdateProjectLane,
}: IProject) {
  const user = useAppSelector((state) => state.user);

  const [laneData, setLaneData] = useState<ILaneData[]>([]);
  const { t } = useTranslation();

  const challengeQuery = useQuery(initUseQuery(api.challenges.get, [forId]));
  const challenge = challengeQuery.data;

  const canManageChallenges = hasPermission(
    user,
    "org.manageChallenges",
    challenge?.organisation,
  );

  const canViewProjectBoard =
    projectManagementVisibility === "users" ||
    util.hasPermission(user, "challenge.viewProjectBoard", forId);

  const projectLaneQuery = useQuery(
    initUseQuery(api.boards.getProjectLanes, [forId!], {
      isEnabled: canManageChallenges || canViewProjectBoard,
    }),
  );
  const lanes = useMemo(
    () => projectLaneQuery.data?.lanes || [],
    [projectLaneQuery.data?.lanes],
  );

  const debouncedImpacts = useThrottle(api.challenges.getIdeaImpacts, 400, [], {
    useLeadingCall: true,
  });
  const queryParams = useIdeaQueryParams("board");
  const ideaImpactsQuery = useQuery(
    initUseQuery(debouncedImpacts, [forId, queryParams]),
  );

  const onSelectIdeas = (
    ideas: OpenAPI.Schemas["Idea"][],
    toSelect: boolean,
  ) => {
    if (toSelect) {
      setSelectedIdeas((prev) => {
        const newIdeas = ideas.filter(
          (idea) => !prev.find((i) => i._id === idea._id),
        );
        return [...prev, ...newIdeas];
      });
    } else {
      setSelectedIdeas((prev) =>
        prev.filter((idea) => !ideas.map((i) => i._id).includes(idea._id)),
      );
    }
  };

  const getLaneData = useCallback(() => {
    const cards: IIdeaCard[] = ideas
      ?.map((idea) => {
        const board = idea?.projectManagement?.boards?.find(
          (b) => b.forId === idea.challenge,
        );

        const visibleImpacts = ideaImpactsQuery.data?.ideaImpacts[
          idea._id
        ]?.totalImpacts?.filter((impact) => impact.enabled && impact.isPinned);

        const ret: IIdeaCard = {
          id: idea._id!,
          idea: {
            ...idea,
            ownerIdeaTemplate:
              (idea as ProjectBoardIdea).ownerIdeaTemplate ||
              challenge?.ownerIdeaTemplate,
          } as ProjectBoardIdea,
          lane: board?.lane,
          order: board?.order || 0,
          impacts: { impactCount: visibleImpacts?.length || 0 },
        };
        if (board?.lane) {
          const cardLane = lanes.find((l) => l._id === board.lane);
          ret.laneMetaType = cardLane?.metaType || "normal";
        }
        if (
          !canManageChallenges &&
          !util.hasPermission(
            user,
            "challenge.moveProjectIdeas",
            challenge?._id,
          ) &&
          !(board?.assignees && board.assignees.indexOf(user!._id) > -1)
        ) {
          ret.draggingDisabled = true;
        }
        return ret;
      })
      .sort((a, b) => (a.order > b.order ? 1 : -1));

    const newLanes: ILaneData[] =
      !laneFilter?.length || laneFilter.includes("default")
        ? [
            {
              id: "default",
              order: 0,
              title: defaultLane?.name || "Default",
              description: defaultLane?.description || "",
              label: "Default",
              cards: cards.filter((i) => !i.lane),
              draggingDisabled: true,
            },
          ]
        : [];

    const canEditProjectBoard =
      canManageChallenges ||
      util.hasPermission(user, "challenge.editProjectBoard", challenge?._id);

    lanes.forEach((l, index) => {
      newLanes.push({
        id: l._id!,
        order: index,
        title: l.name,
        description: l.description,
        metaType: l.metaType,
        cards: cards.filter((i) => i.lane === l._id),
        draggingDisabled: !canEditProjectBoard,
      });
    });

    const ideaOrder: string[] = [];
    newLanes.forEach((lane) => {
      const { cards: newCards = [] } = lane;
      newCards.forEach((card) => {
        const { idea } = card;
        ideaOrder.push(idea._id);
      });
    });
    setLaneData(newLanes);
  }, [
    ideas,
    laneFilter,
    defaultLane?.name,
    defaultLane?.description,
    ideaImpactsQuery.data?.ideaImpacts,
    lanes,
    canManageChallenges,
    user,
    challenge,
  ]);

  useEffect(() => {
    getLaneData();
  }, [getLaneData]);

  const [setupModalOpen, setSetupModalOpen] = useState<boolean>(false);

  const projectWebsocket = useRef<SubscribedWebsocket | null>(null);
  const userId = user?._id;

  const handleWebsocketData = useCallback(
    (data: {
      lanes: OpenAPI.Schemas["ProjectLane"][];
      ideas: OpenAPI.Schemas["Idea"][];
      channel: string;
      fromUser: string;
    }) => {
      const { channel, fromUser } = data;

      if (fromUser === userId) {
        return;
      }

      switch (channel) {
        case "lanesUpdated":
          api.invalidateQueries(api.boards.getProjectLanes, [forId]);
          break;
        case "ideasUpdated":
          api.invalidateQueries(api.challenges.getIdeas, [forId]);
          break;
        default:
          break;
      }
    },
    [userId, forId],
  );

  const canCreateLane =
    canManageChallenges ||
    util.hasPermission(user, "challenge.editProjectBoard", challenge?._id);
  const createLane = useCallback(() => {
    const unnamedLanes = lanes.filter(
      (l) => (l.name || "").indexOf("New lane ") > -1,
    );
    api.boards.createProjectLane(
      forId,
      { name: `New lane ${unnamedLanes.length + 1}` },
      (lane) => {
        api.setQueryData(api.boards.getProjectLanes, [forId], (prev) => ({
          ...prev,
          lanes: [...(prev?.lanes || []), lane],
        }));
        projectWebsocket?.current?.publish?.("lanesUpdated");
      },
      (err) => toast.error(err.message),
    );
  }, [forId, lanes]);

  useEffect(() => {
    if (!forId) return;
    // @ts-ignore
    projectWebsocket.current = websocketApi.subscribe(
      `projectBoard-${forId}`,
      (data) => handleWebsocketData(data),
    );
    return () => {
      if (projectWebsocket.current) {
        projectWebsocket.current.unsubscribe();
      }
    };
  }, [handleWebsocketData, forId]);

  if (!user)
    return (
      <PageLoadError
        title={"Please log in to view the project board"}
        message={undefined}
      />
    );
  // Need to check if the user has the permission to view the project board or if the challenge project management visibility is set to users
  if (!canManageChallenges && !canViewProjectBoard) return null;

  return (
    <>
      <ProjectBoardSetup
        forId={forId}
        lanes={projectLaneQuery.data?.lanes || []}
        dataLoaded={projectLaneQuery.isSuccess}
        setLanes={(newLanes) => {
          api.setQueryData(api.boards.getProjectLanes, [forId], (prev) => ({
            ...prev,
            lanes: newLanes,
          }));
        }}
        setupModalOpen={setupModalOpen}
        setSetupModalOpen={setSetupModalOpen}
        laneFilter={laneFilter}
        canManageChallenges={canManageChallenges}
      />
      <StyledProjectBoardContainer>
        <StyledLaneContainer>
          {projectLaneQuery.isLoading || !challengeQuery.isSuccess ? (
            <LoadingContainer>
              <Loader size="small" />
              <span>Loading project board...</span>
            </LoadingContainer>
          ) : (
            <>
              {!ideas?.length && !ideasLoading ? (
                <Message>
                  <MessageHeader>No {t("generic.ideas")} found</MessageHeader>
                  <MessageContent>
                    Please adjust your filters or wait until{" "}
                    {t("generic.ideas")} are added to the{" "}
                    {t("generic.challenge")}.
                  </MessageContent>
                </Message>
              ) : null}
              <ProjectBoardContext.Provider
                value={{
                  forId,
                  canEditProjectBoard:
                    canManageChallenges ||
                    util.hasPermission(
                      user,
                      "challenge.editProjectBoard",
                      challenge?._id,
                    ),
                  lanes,
                  loading: ideasLoading,
                  setPreviewId,
                  isSelecting,
                  selectedIdeas,
                  selectIdeas: onSelectIdeas,
                  projectWebsocket,
                  checkUpdateProjectLane,
                }}
              >
                <Board
                  loading={ideasLoading}
                  lanes={laneData}
                  setLanes={setLaneData}
                  onCardMove={(ideaId, laneId, position) => {
                    checkUpdateProjectLane([ideaId], laneId, position);
                  }}
                  onLaneMove={(fromIndex, toIndex) => {
                    // indexes have to be corrected by "-1" because the default lane is not included in the lanes array
                    const selectedLane = lanes[fromIndex - 1];
                    if (!selectedLane) return;

                    // Better UX if we update before API call, then reset if it fails
                    api.boards.updateProjectLane(
                      forId,
                      selectedLane._id,
                      { order: toIndex - 1 } as ProjectBoardLaneUpdateBody,
                      () => {
                        projectWebsocket?.current?.publish?.("lanesUpdated");
                      },
                      () => {
                        toast.error("Failed to move lane");
                      },
                    );
                  }}
                  createLane={canCreateLane ? createLane : undefined}
                />
              </ProjectBoardContext.Provider>
            </>
          )}
        </StyledLaneContainer>
      </StyledProjectBoardContainer>
    </>
  );
}

export default Project;
